import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ModuleFeature {
  icon: string;
  title: string;
  description: string;
}

interface ModuleLandingProps {
  name: string;
  tagline: string;
  description: string;
  heroImageUrl: string;
  features: ModuleFeature[];
  ctaText: string;
  ctaAction?: () => void;
  ctaHref?: string;
  secondaryCta?: { text: string; action?: () => void };
  accentColor?: 'primary' | 'accent' | 'secondary';
  relatedModules?: { name: string; path: string; icon: string }[];
}

function ModuleLanding({
  name,
  tagline,
  description,
  heroImageUrl,
  features,
  ctaText,
  ctaAction,
  ctaHref,
  secondaryCta,
  accentColor = 'primary',
  relatedModules,
}: ModuleLandingProps) {
  const navigate = useNavigate();

  const accentMap = {
    primary: { text: 'text-primary-400', bg: 'bg-primary-500', bgSoft: 'bg-primary-500/10', border: 'border-primary-400/40', hover: 'hover:bg-primary-400' },
    accent: { text: 'text-accent-400', bg: 'bg-accent-500', bgSoft: 'bg-accent-500/10', border: 'border-accent-400/40', hover: 'hover:bg-accent-400' },
    secondary: { text: 'text-secondary-400', bg: 'bg-secondary-500', bgSoft: 'bg-secondary-500/10', border: 'border-secondary-400/40', hover: 'hover:bg-secondary-400' },
  };

  const a = accentMap[accentColor];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src={heroImageUrl}
            alt={name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16">
          <div className="max-w-3xl">
            <span className={`inline-block text-sm font-semibold ${a.text} uppercase tracking-wider mb-4`}>
              WorldApeiron Écosystème
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-4 leading-tight">
              World<span className={a.text}>{name}</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground-400 mb-3">
              {tagline}
            </p>
            <p className="text-base text-foreground-400 max-w-xl mb-8 leading-relaxed">
              {description}
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {ctaHref ? (
                <a
                  href={ctaHref}
                  className={`px-8 py-3.5 ${a.bg} text-foreground-950 font-semibold rounded-lg ${a.hover} transition-colors whitespace-nowrap text-sm md:text-base inline-block`}
                >
                  {ctaText}
                </a>
              ) : (
                <button
                  onClick={ctaAction || (() => navigate('/auth/register'))}
                  className={`px-8 py-3.5 ${a.bg} text-foreground-950 font-semibold rounded-lg ${a.hover} transition-colors whitespace-nowrap text-sm md:text-base`}
                >
                  {ctaText}
                </button>
              )}
              {secondaryCta && (
                <button
                  onClick={secondaryCta.action || (() => navigate('/'))}
                  className="px-8 py-3.5 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm md:text-base"
                >
                  {secondaryCta.text}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <span className={`inline-block text-sm font-semibold ${a.text} uppercase tracking-wider mb-3`}>
              Fonctionnalités
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Tout ce dont vous avez besoin
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${a.bgSoft} mb-4`}>
                  <i className={`${feature.icon} text-2xl ${a.text}`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Modules */}
      {relatedModules && relatedModules.length > 0 && (
        <section className="py-16 md:py-20 bg-background-50 border-t border-background-200/70">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-8 text-center">
              Découvrez aussi
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {relatedModules.map((mod) => (
                <button
                  key={mod.name}
                  onClick={() => navigate(mod.path)}
                  className="flex items-center gap-3 px-5 py-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10">
                    <i className={`${mod.icon} text-xl text-primary-400`} />
                  </div>
                  <span className="font-heading font-semibold text-sm text-foreground-50">
                    {mod.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

export default ModuleLanding;